import React from "react";
import { XpCareerSection } from "../Xp/XpCareerSection";
import type { XpSummary } from "../../utils/xp";
import { CAREER_STAT_ICONS } from "./CareerStatCardHeader";

type SectionCell = {
  label: string;
  value: React.ReactNode;
};

function StatCell({ label, value }: SectionCell) {
  return (
    <div className="flex min-h-[74px] flex-col items-center justify-center bg-[#2f1455] px-2 py-2 text-center">
      <p className="font-anton text-[12px] font-normal uppercase leading-[12px] tracking-normal text-white/35 [leading-trim:none]">
        {label}
      </p>
      <p className="mt-2 font-anton text-[16px] font-normal uppercase leading-none tracking-normal text-white tabular-nums [leading-trim:none]">
        {value}
      </p>
    </div>
  );
}

function Header({ iconSrc, title }: { iconSrc: string; title: string }) {
  return (
    <div className="flex h-[38px] items-center gap-2 bg-[#5f2bb0] px-3">
      <img src={iconSrc} alt="" className="h-5 w-5 shrink-0 object-contain" aria-hidden />
      <span className="font-anton text-[17px] font-normal uppercase leading-none tracking-normal text-white">{title}</span>
    </div>
  );
}

type CareerStatsStackProps = {
  xp?: XpSummary | null;
  showXpHistoryLink?: boolean;
  trader: {
    played: number;
    won: number;
    totalWinnings: number;
    currentStreak?: number;
    bestStreak?: number;
    biggestWin?: number;
  };
  squares: { played: number; won: number; totalWinnings: number };
  bullion: { played: number; won: number; totalWinnings: number };
  sixes: { played: number; totalWinnings: number; biggestWin: number };
  leagues: { played: number; won: number; totalWinnings: number };
  quickfire: { plays: number; correct: number; totalWinnings: number };
  formatCoins: (n: number) => string;
};

export function CareerStatsStack({
  xp,
  showXpHistoryLink = true,
  trader,
  squares,
  bullion,
  sixes,
  leagues,
  quickfire,
  formatCoins,
}: CareerStatsStackProps) {
  return (
    <section className="mb-4 pt-2">
      <h2 className="mb-4 text-center font-anton text-[15px] font-normal uppercase leading-[12px] tracking-normal text-white [leading-trim:none]">
        Career stats
      </h2>

      <div className="space-y-2">
        <XpCareerSection xp={xp ?? null} showHistoryLink={showXpHistoryLink} />

        <div className="overflow-hidden border border-white/10">
          <Header iconSrc={CAREER_STAT_ICONS.leagues} title="Leagues" />
          <div className="grid grid-cols-3 gap-[2px] border-t border-white/5 bg-[#3a1a64]">
            <StatCell label="Played" value={leagues.played} />
            <StatCell label="Won" value={leagues.won} />
            <StatCell
              label="Career total"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-coin.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(leagues.totalWinnings)}
                </span>
              }
            />
          </div>
        </div>

        <div className="overflow-hidden border border-white/10">
          <Header iconSrc={CAREER_STAT_ICONS.bullion} title="Bullion" />
          <div className="grid grid-cols-3 gap-[2px] border-t border-white/5 bg-[#3a1a64]">
            <StatCell label="Played" value={bullion.played} />
            <StatCell label="Won" value={bullion.won} />
            <StatCell
              label="Career total"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-gold-ball.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(bullion.totalWinnings)}
                </span>
              }
            />
          </div>
        </div>

        <div className="overflow-hidden border border-white/10">
          <Header iconSrc={CAREER_STAT_ICONS.sixes} title="Sixes" />
          <div className="grid grid-cols-3 gap-[2px] border-t border-white/5 bg-[#3a1a64]">
            <StatCell label="Played" value={sixes.played} />
            <StatCell
              label="Best result"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-gold-ball.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(Math.max(0, Number(sixes.biggestWin ?? 0)))}
                </span>
              }
            />
            <StatCell
              label="Career total"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-gold-ball.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(sixes.totalWinnings)}
                </span>
              }
            />
          </div>
        </div>

        <div className="overflow-hidden border border-white/10">
          <Header iconSrc={CAREER_STAT_ICONS.trader} title="Trader" />
          <div className="grid grid-cols-3 gap-[2px] border-t border-white/5 bg-[#3a1a64]">
            <StatCell label="Played" value={trader.played} />
            <StatCell label="Won" value={trader.won} />
            <StatCell
              label="Career total"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-gold-ball.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(trader.totalWinnings)}
                </span>
              }
            />
          </div>
          <div className="mt-[2px] grid grid-cols-3 gap-[2px] border-t border-white/5 bg-[#3a1a64]">
            <StatCell label="Current streak" value={`${Math.max(0, Math.trunc(trader.currentStreak ?? 0))} wins`} />
            <StatCell label="Best streak" value={`${Math.max(0, Math.trunc(trader.bestStreak ?? 0))} wins`} />
            <StatCell
              label="Best result"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-gold-ball.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(Math.max(0, Number(trader.biggestWin ?? 0)))}
                </span>
              }
            />
          </div>
        </div>

        <div className="overflow-hidden border border-white/10">
          <Header iconSrc={CAREER_STAT_ICONS.squares} title="Squares" />
          <div className="grid grid-cols-3 gap-[2px] border-t border-white/5 bg-[#3a1a64]">
            <StatCell label="Played" value={squares.played} />
            <StatCell label="Won" value={squares.won} />
            <StatCell
              label="Career total"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-gold-ball.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(squares.totalWinnings)}
                </span>
              }
            />
          </div>
        </div>

        <div className="overflow-hidden border border-white/10">
          <Header iconSrc={CAREER_STAT_ICONS.quickfire} title="Quickfire" />
          <div className="grid grid-cols-3 gap-[2px] border-t border-white/5 bg-[#3a1a64]">
            <StatCell label="Plays" value={quickfire.plays} />
            <StatCell label="Correct" value={quickfire.correct} />
            <StatCell
              label="Career total"
              value={
                <span className="inline-flex items-center gap-1">
                  <img src="/assets/currency-gold-ball.png" alt="" className="h-4 w-4 shrink-0 object-contain" aria-hidden />
                  {formatCoins(quickfire.totalWinnings)}
                </span>
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
}

